import {
  resultsToCSV,
  standardConditions,
  summariesToCSV,
  TrialRunner,
  type ConditionSummary,
  type TrialResult,
} from '../experiments/TrialRunner.ts';
import type { GameConfig } from '../game/GameState.ts';
import { button, downloadText, el, numberField, panel } from './dom.ts';

export interface ComparisonPanelCallbacks {
  /** Stop the live animation loop; the runner owns the clock while it runs. */
  onBeforeRun: () => void;
  onAfterRun: () => void;
  getGameConfig: () => Partial<GameConfig>;
}

/**
 * Runs the standard condition set headlessly and tabulates it.
 *
 * The same simulation the user is watching, on a virtual clock: seventy
 * 60-second trials take about a second. That is what makes "insert 100 ms of
 * delay, then turn compensation off, then look at the difference" a question
 * you can answer while the participant is still in the chair, rather than an
 * afternoon of realtime runs.
 */
export class ComparisonPanel {
  readonly root: HTMLElement;

  private runBtn: HTMLButtonElement;
  private exportBtn: HTMLButtonElement;
  private statusEl: HTMLElement;
  private tableWrap: HTMLElement;
  private seedsInput: { row: HTMLElement; set: (v: number) => void };
  private durationInput: { row: HTMLElement; set: (v: number) => void };

  private seeds = 8;
  private durationSec = 60;
  private results: TrialResult[] = [];
  private summaries: ConditionSummary[] = [];
  private running = false;

  constructor(private readonly cb: ComparisonPanelCallbacks) {
    const p = panel('Condition comparison');
    this.root = p.root;

    p.body.appendChild(
      el(
        'p',
        'hint',
        'Headless replay of the standard conditions on a virtual clock. The keyboard ' +
          'condition is missing on purpose: it needs a person, so record it as a live ' +
          'session and compare the exported summary against these rows.',
      ),
    );

    this.seedsInput = numberField('Seeds per condition', this.seeds, (v) => {
      this.seeds = Math.max(1, Math.round(v));
    }, { min: 1, max: 50, step: 1 });
    this.durationInput = numberField('Trial duration', this.durationSec, (v) => {
      this.durationSec = Math.max(5, Math.round(v));
    }, { min: 5, max: 600, step: 5, unit: 's' });
    p.body.append(this.seedsInput.row, this.durationInput.row);

    const row = el('div', 'button-row');
    this.runBtn = button('Run comparison', () => void this.run(), 'btn-primary');
    this.exportBtn = button('Export CSV', () => this.exportCsv(), 'btn-secondary');
    this.exportBtn.disabled = true;
    row.append(this.runBtn, this.exportBtn);
    p.body.appendChild(row);

    this.statusEl = el('div', 'comparison-status', 'Not run yet.');
    this.tableWrap = el('div', 'comparison-table-wrap');
    p.body.append(this.statusEl, this.tableWrap);
  }

  private async run(): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.runBtn.disabled = true;
    this.exportBtn.disabled = true;
    this.statusEl.textContent = 'Running…';
    this.cb.onBeforeRun();

    try {
      const runner = new TrialRunner();
      const t0 = performance.now();
      const { results, summaries } = await runner.run(standardConditions(), {
        seeds: Array.from({ length: this.seeds }, (_, i) => i + 1),
        durationSec: this.durationSec,
        game: this.cb.getGameConfig(),
        yieldBetweenTrials: true,
        onProgress: (done, total) => {
          this.statusEl.textContent = `Running… ${done}/${total} trials`;
        },
      });
      this.results = results;
      this.summaries = summaries;
      this.render();
      this.statusEl.textContent =
        `${results.length} trials in ${((performance.now() - t0) / 1000).toFixed(1)} s`;
      this.exportBtn.disabled = false;
    } catch (err) {
      this.statusEl.textContent = `Failed: ${String(err)}`;
    } finally {
      this.running = false;
      this.runBtn.disabled = false;
      this.cb.onAfterRun();
    }
  }

  private render(): void {
    const columns: [string, (s: ConditionSummary) => string][] = [
      ['condition', (s) => s.label],
      ['mean score', (s) => s.meanScore.toFixed(1)],
      ['max', (s) => String(s.maxScore)],
      ['survival', (s) => `${(s.meanSurvivalMs / 1000).toFixed(1)} s`],
      ['deaths', (s) => `${s.deaths}/${s.trials}`],
      ['flaps/s', (s) => (Number.isFinite(s.flapsPerSec) ? s.flapsPerSec.toFixed(2) : '—')],
      ['stim/s', (s) => (Number.isFinite(s.stimPerSec) ? s.stimPerSec.toFixed(2) : '—')],
      ['success', (s) => (Number.isFinite(s.commandSuccessRate) ? `${(s.commandSuccessRate * 100).toFixed(0)}%` : '—')],
      ['missed', (s) => (Number.isFinite(s.meanMissed) ? s.meanMissed.toFixed(1) : '—')],
      ['latency', (s) => (Number.isFinite(s.latencyMedianMs) ? `${s.latencyMedianMs.toFixed(0)} ms` : '—')],
      ['jitter', (s) => (Number.isFinite(s.latencyJitterMs) ? `${s.latencyJitterMs.toFixed(0)} ms` : '—')],
    ];

    const table = el('table', 'comparison-table');
    const thead = el('thead');
    const hrow = el('tr');
    for (const [head] of columns) hrow.appendChild(el('th', '', head));
    thead.appendChild(hrow);
    const tbody = el('tbody');
    for (const s of this.summaries) {
      const tr = el('tr');
      columns.forEach(([, get], i) => {
        const td = el('td', i === 0 ? 'condition-cell' : '');
        td.textContent = get(s);
        tr.appendChild(td);
      });
      if (s.deaths === s.trials) tr.className = 'row-bad';
      else if (s.deaths === 0) tr.className = 'row-good';
      tbody.appendChild(tr);
    }
    table.append(thead, tbody);
    this.tableWrap.replaceChildren(table);
  }

  private exportCsv(): void {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    downloadText(`comparison-summary-${stamp}.csv`, summariesToCSV(this.summaries), 'text/csv');
    downloadText(`comparison-trials-${stamp}.csv`, resultsToCSV(this.results), 'text/csv');
  }
}
